import { useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar, SnackbarStatus } from '@weareopenr/component-library';
import { updateInstance, IUpdateInstanceRequest, IExtensionInstance } from 'src/api/extensions';
import { useUserTracking } from '../useUserTracking/useUserTracking';
import { QueryKeys } from 'src/constants/QueryKeys';

interface IUpdateInstanceParams {
  brandId: string;
  extensionId: string;
  instanceId: string;
  payload: IUpdateInstanceRequest;
}

export const useUpdateInstance = (onSuccess?: (data: IExtensionInstance) => void) => {
  const { showSnackbar } = useSnackbar();
  const { captureException } = useUserTracking();
  const queryClient = useQueryClient();

  // Use ref to always have latest callback without recreating mutation
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  return useMutation({
    mutationFn: ({ brandId, extensionId, instanceId, payload }: IUpdateInstanceParams) =>
      updateInstance(brandId, extensionId, instanceId, payload),
    onSuccess: (data: IExtensionInstance, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.EXTENSIONS, variables.brandId] });
      const instanceName = data?.name || 'Instance';
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `${instanceName} has been updated.`,
      });
      onSuccessRef.current?.(data);
    },
    onError: (error: Error) => {
      showSnackbar({
        message: error.message || 'Unable to update instance',
        status: SnackbarStatus.ERROR,
      });
      captureException(error);
    },
  });
};
